import { TNaerasJob, TNaerasJobRun, TNaerasJobStatus, TNaerasPoolSource } from '@/data/types';
import { POOL_SOURCE_META } from './pool';

/**
 * The jobs are scheduled, and report, on the Greek calendar — `split_hour` and
 * every cron time below are Athens hours, so the timestamps have to be read
 * back in the same zone to line up with them.
 */
const ATHENS_TZ = 'Europe/Athens';

export interface JobMeta {
  job: TNaerasJob;
  title: string;
  schedule: string;
  /** What the job does, in one line the operator can act on. */
  description: string;
  /** How to read `run_date` for this job — it does not mean the same for each. */
  runDateMeaning: string;
  /**
   * Athens hour after which no further attempt is expected today, or null where
   * retries aren't daily. Not always the end of the job's own cron window —
   * Job A is also driven by Job B, so its attempts run later than 09:30.
   */
  lastRetryHour: number | null;
}

export const JOB_META: Record<TNaerasJob, JobMeta> = {
  A: {
    job: 'A',
    title: 'Εκκαθάριση',
    schedule: '00:30–09:30 ωριαία (+ Εργασία B)',
    description:
      'Κλείνει τη χθεσινή ημέρα και υπολογίζει το πραγματικό απόθεμα υβριδικής στις 00:00 σήμερα.',
    runDateMeaning:
      'Η ημέρα που αφορά — εκκαθαρίζει την προηγούμενη. Σε αναπλήρωση μπορεί να εκτελεστεί αργότερα.',
    // Job B calls Job A's catch-up on every one of its own ticks, so attempts
    // keep coming until 22:00 — well past Job A's own 09:30 window.
    lastRetryHour: 22,
  },
  B: {
    job: 'B',
    title: 'Υπολογισμός Προσφοράς',
    schedule: '10:00–22:00 ωριαία',
    description:
      'Ζητά από το NAERAS την αυριανή προσφορά. Παραλείπεται όταν η αυριανή προσφορά είναι ήδη οριστική. Μια χαμένη μέρα δεν αναπληρώνεται.',
    runDateMeaning:
      'Η ημέρα που αφορά — δίνει προσφορά για την επόμενη. Σε αναπλήρωση μπορεί να εκτελεστεί αργότερα.',
    lastRetryHour: 22,
  },
  C: {
    job: 'C',
    title: 'Επανεκπαίδευση Μοντέλου',
    schedule: '03:00, ημέρες 1–7 του μήνα',
    description:
      'Επανεκπαιδεύει το μοντέλο πρόβλεψης φορτίου με όλο το ιστορικό ΗΕΠ.',
    runDateMeaning: 'Η 1η του μήνα που εκπαιδεύτηκε, όχι η ημέρα εκτέλεσης.',
    lastRetryHour: null,
  },
  D: {
    job: 'D',
    title: 'Ingest ΗΕΠ',
    schedule: '12:30–23:30 ωριαία',
    description:
      'Κατεβάζει τα ημερήσια αρχεία ΗΕΠ του ΔΕΔΔΗΕ. Τροφοδοτεί μόνο την πρόβλεψη φορτίου, όχι το απόθεμα.',
    runDateMeaning: 'Η ημέρα που έτρεξε — ελέγχει τις τελευταίες 14 ημέρες.',
    lastRetryHour: 23,
  },
};

export const JOB_ORDER: TNaerasJob[] = ['A', 'B', 'C', 'D'];

/**
 * ClickHouse hands back `started_at` / `updated_at` as "YYYY-MM-DD HH:mm:ss.SSS"
 * in **UTC**, with no zone marker — so `new Date()` would read them as local
 * time and slide them by the offset. A run stamped 19:00:00 with
 * `split_hour: 22` is the 22:00 Athens attempt, not a 19:00 one.
 */
export function parseJobDate(value: string | null | undefined): Date | null {
  if (!value) return null;

  const iso = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);

  return Number.isNaN(date.getTime()) ? null : date;
}

/** "22:00:36", Athens. */
export function formatAthensTime(date: Date): string {
  return new Intl.DateTimeFormat('el-GR', {
    timeZone: ATHENS_TZ,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

/** "24/09 22:00:36", Athens. */
export function formatAthensDateTime(date: Date): string {
  const parts = new Intl.DateTimeFormat('el-GR', {
    timeZone: ATHENS_TZ,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';

  return `${get('day')}/${get('month')} ${get('hour')}:${get('minute')}:${get('second')}`;
}

/** The current hour in Athens, for deciding whether a retry is still due. */
function athensHour(): number {
  return Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: ATHENS_TZ,
      hour: '2-digit',
      hour12: false,
    }).format(new Date())
  );
}

export interface JobOutcome {
  label: string;
  /** Same meaning in as few characters as possible, for narrow cards. */
  short: string;
  /** Drives the badge palette — `warning` is "not final yet", not a failure. */
  tone: 'success' | 'warning' | 'danger' | 'neutral';
}

/**
 * How a run should read to an operator.
 *
 * Four rules from the job design that a raw status would get wrong: a Job C
 * result of `rejected` is a *successful* run — the retrained model simply
 * didn't beat the current one; a `skipped` Job B had nothing to do, because the
 * day it would have bid for was already final; a `running` row left behind by a
 * crash is not blocking anything; and a failure with a scheduled retry still
 * ahead of it today is not yet final, so none of the four deserves a red badge.
 */
export function jobOutcome(run: TNaerasJobRun, todayStr?: string): JobOutcome {
  if (run.status === 'success' && run.job === 'C' && run.result?.state === 'rejected') {
    return { label: 'Απορρίφθηκε (κανονικό)', short: 'Απορρίφθηκε', tone: 'neutral' };
  }

  if (run.status === 'skipped') {
    const target = run.result?.target_date;

    return {
      label: target
        ? `Παραλείφθηκε — η προσφορά της ${target} ήταν ήδη οριστική`
        : 'Παραλείφθηκε — η προσφορά ήταν ήδη οριστική',
      short: 'Παραλείφθηκε',
      tone: 'neutral',
    };
  }

  if (run.status === 'success') {
    return { label: 'Επιτυχία', short: 'Επιτυχία', tone: 'success' };
  }

  if (run.status === 'running') {
    return { label: 'Σε εξέλιξη', short: 'Σε εξέλιξη', tone: 'warning' };
  }

  const meta = JOB_META[run.job];
  const lastRetryHour = meta ? meta.lastRetryHour : null;
  const retryStillDue =
    todayStr === run.run_date && lastRetryHour !== null && athensHour() < lastRetryHour;

  return retryStillDue
    ? { label: 'Αποτυχία — εκκρεμεί επανάληψη', short: 'Επανάληψη', tone: 'warning' }
    : { label: 'Αποτυχία', short: 'Αποτυχία', tone: 'danger' };
}

export const STATUS_FILTERS: { value: TNaerasJobStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Όλες' },
  { value: 'success', label: 'Επιτυχία' },
  { value: 'running', label: 'Σε εξέλιξη' },
  { value: 'failed', label: 'Αποτυχία' },
  { value: 'skipped', label: 'Παραλείφθηκε' },
];

export interface HepGroup {
  label: string;
  /** Dates, or "YYYY-MM-DD: reason" strings for `failed`. */
  values: string[];
  tone: 'success' | 'warning' | 'danger';
  hint: string;
}

/**
 * Job D's result, grouped by what each list means to an operator.
 *
 * A file that simply isn't published yet is the normal case — ΔΕΔΔΗΕ posts each
 * day at about 14:30, and the weekend's two files on Monday — so `not_due` and
 * `pending` must not read as failures. Only `overdue` and `failed` are.
 */
export function summariseHepResult(run: TNaerasJobRun): HepGroup[] {
  const result = run.result;
  if (!result) return [];

  const groups: HepGroup[] = [
    {
      label: 'Αποθηκεύτηκαν',
      values: result.stored ?? [],
      tone: 'success',
      hint: 'Κατέβηκαν και αποθηκεύτηκαν σε αυτή την εκτέλεση.',
    },
    {
      label: 'Δεν έχουν δημοσιευτεί ακόμη',
      values: result.not_due ?? [],
      tone: 'warning',
      hint: 'Κανονικό — ο ΔΕΔΔΗΕ δεν τα έχει δημοσιεύσει ακόμη.',
    },
    {
      label: 'Σε αναμονή',
      values: result.pending ?? [],
      tone: 'warning',
      hint: 'Πέρασε η αναμενόμενη ώρα, αλλά υπάρχει περιθώριο 2 ημερών.',
    },
    {
      label: 'Δεν δημοσιεύτηκαν ποτέ',
      values: result.overdue ?? [],
      tone: 'danger',
      hint: 'Ξεπέρασαν το περιθώριο — κάνουν την εκτέλεση αποτυχημένη.',
    },
    {
      label: 'Σφάλματα ανάγνωσης',
      values: result.failed ?? [],
      tone: 'danger',
      hint: 'Το αρχείο υπάρχει αλλά δεν διαβάστηκε ή δεν αποθηκεύτηκε.',
    },
  ];

  return groups.filter((group) => group.values.length > 0);
}

/**
 * A failed Job A or B of the last 5 days carries a `result` filled with zeros
 * so the ledger has a row of the right shape. The run is still a failure and
 * the zeros were never measured, so they must not read as results.
 */
export const isZeroFilled = (run: TNaerasJobRun) => run.result?.zero_filled === true;

/**
 * The handful of `result` fields worth reading per job, as label/value pairs.
 * The rest stays behind the raw JSON toggle rather than bloating the row.
 */
export function summariseResult(run: TNaerasJobRun): { label: string; value: string }[] {
  const result = run.result;
  if (!result) return [];

  const show = (value: unknown) =>
    value === null || value === undefined ? '—' : String(value);
  const mwh = (value: unknown) =>
    value === null || value === undefined ? '—' : `${Number(value).toFixed(2)} MWh`;
  const poolSource = (value: unknown) =>
    POOL_SOURCE_META[value as TNaerasPoolSource]?.label ?? show(value);

  // Only these few values are real in a zero-filled row; the rest are 0 by fill
  if (isZeroFilled(run)) {
    return run.job === 'A'
      ? [
          { label: 'Εκκαθαρίστηκε', value: show(result.reconciled_date) },
          { label: 'Απόθεμα πριν', value: mwh(result.current_pool_mwh) },
          { label: 'Πηγή αποθέματος', value: poolSource(result.pool_source) },
        ]
      : [
          { label: 'Ημέρα προσφοράς', value: show(result.target_date) },
          { label: 'Σενάριο', value: show(result.scenario) },
          { label: 'Ώρα διαχωρισμού', value: show(result.split_hour) },
          { label: 'Απόθεμα πριν', value: mwh(result.current_pool_mwh) },
          { label: 'Πηγή αποθέματος', value: poolSource(result.pool_source) },
          { label: 'Καθυστέρηση εκκαθάρισης', value: `${show(result.pool_stale_days)} ημέρες` },
        ];
  }

  if (run.job === 'A') {
    return [
      { label: 'Εκκαθαρίστηκε', value: show(result.reconciled_date) },
      { label: 'Πραγματικό απόθεμα 00:00', value: mwh(result.hybrid_pool_00_real) },
      { label: 'Εκροή', value: mwh(result.outflow_mwh) },
      { label: 'Πηγή αποθέματος', value: poolSource(result.pool_source) },
      { label: 'Κανόνας', value: show(result.rule) },
    ];
  }

  if (run.job === 'B') {
    // A skipped run never called NAERAS, so none of the usual inputs exist.
    if (run.status === 'skipped') {
      return [
        { label: 'Ημέρα προσφοράς', value: show(result.target_date) },
        { label: 'Λόγος', value: 'Η προσφορά ήταν ήδη οριστική' },
      ];
    }

    // The very first Job B seeds the state rather than bidding; its numbers
    // live under `inputs` and were never submitted anywhere.
    if (result.bootstrap) {
      return [
        { label: 'Αρχικοποίηση', value: 'Δεν υποβλήθηκε ποτέ' },
        { label: 'Ημέρα προσφοράς', value: show(result.target_date) },
        { label: 'Απόθεμα 00:00', value: mwh(result.inputs?.hybrid_pool_mwh) },
        { label: 'Πηγή αποθέματος', value: poolSource(result.inputs?.pool_source) },
        { label: 'Πρόβλεψη', value: show(result.inputs?.forecast_source) },
        { label: 'Ποιότητα πρόβλεψης', value: show(result.inputs?.forecast_quality) },
      ];
    }

    // `is_winter` is today's season; the bid's own season is on the bid
    // itself, and the two differ across a season change or a closed switch
    const bidIsWinter = Array.isArray(result.bid) ? result.bid[0]?.is_winter : undefined;
    const season = (isWinter: unknown) =>
      isWinter == null ? show(undefined) : isWinter ? 'Υγρή' : 'Ξηρή';

    return [
      { label: 'Ημέρα προσφοράς', value: show(result.target_date) },
      ...(bidIsWinter != null
        ? [{ label: 'Περίοδος προσφοράς', value: season(bidIsWinter) }]
        : []),
      { label: 'Σενάριο', value: show(result.scenario) },
      { label: 'Ώρα διαχωρισμού', value: show(result.split_hour) },
      { label: 'Περίοδος σήμερα', value: season(result.is_winter) },
      { label: 'Απόθεμα 00:00', value: mwh(result.hybrid_pool_00) },
      { label: 'Πηγή αποθέματος', value: poolSource(result.pool_source) },
      { label: 'Καθυστέρηση εκκαθάρισης', value: `${show(result.pool_stale_days)} ημέρες` },
      { label: 'Εκτιμ. εκροή υβριδικής', value: mwh(result.est_hybrid_outflow_mwh) },
      { label: 'Πρόβλεψη', value: show(result.forecast?.source) },
      { label: 'Ποιότητα πρόβλεψης', value: show(result.forecast?.quality) },
      { label: 'Κανόνας', value: show(result.rule) },
    ];
  }

  if (run.job === 'D') {
    return [
      { label: 'Παράθυρο ελέγχου', value: `${show(result.window_days)} ημέρες` },
      { label: 'Ελλιπείς στην έναρξη', value: String((result.missing ?? []).length) },
    ];
  }

  return [
    { label: 'Αποτέλεσμα', value: show(result.state) },
    { label: 'Παρατηρήσεις', value: show(result.sent?.n_observations) },
    { label: 'Εκτός ορίων', value: show(result.sent?.out_of_range) },
  ];
}
